import { Button, EpicIcons, H1Span, PathParams } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import moment from "moment";
import { Calendar } from "primereact/calendar";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import React, { useRef } from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { useTranslation } from "@i18n/hooks";
import { messageDispatchForm_MessageDispatchFragment$key } from "@relay/messageDispatchForm_MessageDispatchFragment.graphql";
import { messageDispatchForm_Query } from "@relay/messageDispatchForm_Query.graphql";
import { messageDispatchForm_SendMessageDispatchMutation } from "@relay/messageDispatchForm_SendMessageDispatchMutation.graphql";
import { MessageDispatchFormContextProvider } from "./message-dispatch-form.context";
import {
	MESSAGE_DISPATCH_FRAGMENT,
	QUERY,
	SEND_MESSAGE_MUTATION,
} from "./message-dispatch-form.graphql";
import { headerClass } from "./message-dispatch-form.styles";
import { MessageDispatchFormState } from "./message-dispatch-form.types";
import { ValidatedField } from "../../../../components/ValidatedField";

import { Paths } from "../../../../routes/paths";
import { MessageDispatchesPath } from "../../../message-dispatches";
import { DispatchExclusions } from "../dispatch-exclusions";
import { DispatchInclusions } from "../dispatch-inclusions";

export const MessageDispatchForm = () => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);
	const navigate = useNavigate();

	const { messageDispatchId } = useParams<PathParams<typeof MessageDispatchesPath>>();

	const query = useLazyLoadQuery<messageDispatchForm_Query>(QUERY, {
		id: messageDispatchId ?? "",
		skip: !messageDispatchId || messageDispatchId === "new",
	});

	const data = useFragment<messageDispatchForm_MessageDispatchFragment$key>(
		MESSAGE_DISPATCH_FRAGMENT,
		query ?? null,
	);
	const messageDispatch = data?.node;

	const [sendMessage] =
		useMutation<messageDispatchForm_SendMessageDispatchMutation>(SEND_MESSAGE_MUTATION);

	const handleSendMessage = () => {
		if (!messageDispatchId) return;
		formik.setSubmitting(true);
		sendMessage({
			variables: {
				input: {
					messageDispatchId: messageDispatchId,
					sentAtOpt: formik.values.sendAt ?? null,
				},
			},
			onCompleted: () => {
				formik.setSubmitting(false);
				navigate(Paths.messagesDispatches.path);
			},
			onError: () => {
				toast.current?.show({
					severity: "error",
					life: 3000,
					summary: t("messageDispatch.sendErrorSummary"),
					detail: t("messageDispatch.sendErrorDetail"),
				});
				formik.setSubmitting(false);
			},
		});
	};

	const formik = useFormik<MessageDispatchFormState>({
		initialValues: {
			sendAt: undefined,
		},
		onSubmit: async (values, { setSubmitting }) => {
			handleSendMessage();
			setSubmitting(false);
		},
	});

	const hasNoInclusions = !(messageDispatch?.includes && messageDispatch?.includes?.length > 0);

	return (
		<MessageDispatchFormContextProvider>
			<Toast ref={toast}></Toast>
			<div className={headerClass}>
				<H1Span>
					{t("messageDispatch.sendHeading", {
						title: messageDispatch?.messageDefinition?.internalTitle,
					})}
				</H1Span>
			</div>
			<DispatchInclusions messageDispatchFragmentRef={messageDispatch} />
			<Divider />
			<DispatchExclusions messageDispatchFragmentRef={messageDispatch} />
			<Divider />
			<ValidatedField<MessageDispatchFormState, string>
				name={"sendAt"}
				label={t("messageDispatch.sendAt")}
				component={({ fieldName, fieldValue, updateField }) => (
					<Calendar
						name={fieldName}
						dateFormat={"dd.mm.yy"}
						showTime
						showButtonBar
						value={
							fieldValue
								? moment(fieldValue.replace("[UTC]", "")).toDate()
								: undefined
						}
						onChange={(e) => {
							updateField(
								e.value ? moment(e.value as Date).toISOString(true) : undefined,
							);
						}}
					/>
				)}
				formikConfig={formik}
			/>
			<Button
				icon={EpicIcons.SEND}
				label={t("messageDispatch.sendButton")}
				onClick={formik.submitForm}
				disabled={hasNoInclusions}
			></Button>
		</MessageDispatchFormContextProvider>
	);
};
